import { matchPath } from 'react-router-dom';
import type { Theme } from '@mui/material/styles';
import type { NavMode } from './useNavMode';

// Heights of the fixed bars around the page. Layout publishes the current
// values as two CSS variables on <html>; anything that must fit between the
// bars reads them (CONTENT_HEIGHT) instead of hard-coding the header.
export const TOP_BAR_HEIGHT_PX = 52;
export const HEADER_HEIGHT_PX = 60;
export const SEARCH_ROW_HEIGHT_PX = 56;
export const TAB_BAR_HEIGHT_PX = 56;

/** The More sheet's id: the tab bar's More / Close and the top bar's More point at it (aria-controls). */
export const MORE_SHEET_ID = 'more-sheet';

/** The phone top bar marks its root with this (the dock has DOCK_ATTRIBUTE): the More sheet makes the bar inert by it, not by MUI's class names. */
export const TOP_BAR_ATTRIBUTE = 'data-mobile-top-bar';

// Landscape iPhones: with viewport-fit=cover the page reaches under the notch
// on the left or right. env() is 0 everywhere else (all desktops), so adding
// it to a padding changes nothing there.
export const SAFE_AREA_LEFT = 'env(safe-area-inset-left, 0px)';
export const SAFE_AREA_RIGHT = 'env(safe-area-inset-right, 0px)';
export const padLeft = (px: number) => `calc(${px}px + ${SAFE_AREA_LEFT})`;
export const padRight = (px: number) => `calc(${px}px + ${SAFE_AREA_RIGHT})`;

// The side padding MUI gives a Container or a Toolbar with gutters (16 px,
// 24 px from sm), plus the notch inset. Spelled out per breakpoint because a
// plain padding in sx loses to the component's own media-query gutter from sm
// on, so only a media-query value of the same kind can carry the inset there.
export const GUTTER_PADDING = {
    pl: { xs: padLeft(16), sm: padLeft(24) },
    pr: { xs: padRight(16), sm: padRight(24) },
};

/**
 * How tall the phone search results are: what the visible viewport leaves
 * between the top bar and the field row once the notch inset (env(), so CSS)
 * is taken off. The panel starts right under the top bar, as in the mockup.
 * Never below 160 px.
 */
export const phoneResultsMaxHeight = (viewportHeight: number) =>
    `max(160px, calc(${viewportHeight}px - ${
        TOP_BAR_HEIGHT_PX + SEARCH_ROW_HEIGHT_PX
    }px - env(safe-area-inset-top, 0px)))`;

/**
 * The white surface of the phone mockups (the search field, its results, the
 * More sheet). The app's own paper colour is a light grey, which is not what
 * was approved. Dark mode has no mockup: the theme's paper there.
 */
export const phoneSurface = (theme: Theme) =>
    theme.palette.mode === 'dark' ? theme.palette.background.paper : '#ffffff';

export const CHROME_TOP_VAR = '--irm-chrome-top';
export const CHROME_BOTTOM_VAR = '--irm-chrome-bottom';

const BETWEEN_THE_BARS = `var(${CHROME_TOP_VAR}, ${HEADER_HEIGHT_PX}px) - var(${CHROME_BOTTOM_VAR}, 0px)`;

/** The height left for a page between the bars. Use instead of calc(100vh - 60px). */
export const CONTENT_HEIGHT = `calc(100dvh - ${BETWEEN_THE_BARS})`;

/**
 * Same with vh, for browsers without dvh (before Safari 15.4 / Chrome 108): a
 * `height: calc(100dvh ...)` they cannot parse is dropped, and a full-height
 * page would lose its height altogether.
 */
export const CONTENT_HEIGHT_FALLBACK = `calc(100vh - ${BETWEEN_THE_BARS})`;

/**
 * The height of a full-height page, for an `sx`: spread it where you would
 * write `height: CONTENT_HEIGHT`. A browser without dvh keeps the vh height;
 * one with it gets dvh, which follows the phone's collapsing address bar.
 */
export const CONTENT_HEIGHT_SX = {
    height: CONTENT_HEIGHT_FALLBACK,
    '@supports (height: 100dvh)': { height: CONTENT_HEIGHT },
};

// Where a full-screen view (the chat views) sits: fixed, between the bars.
// Below md it follows the phone bars, notch and home indicator included. From
// md on it starts 2 px under the dense 48 px desktop header, exactly where it
// always did (the page offset of 60 px is not meant here).
export const DESKTOP_FULL_SCREEN_TOP_PX = 50;
export const FULL_SCREEN_INSETS = {
    top: {
        xs: `var(${CHROME_TOP_VAR}, ${TOP_BAR_HEIGHT_PX}px)`,
        md: `${DESKTOP_FULL_SCREEN_TOP_PX}px`,
    },
    bottom: `var(${CHROME_BOTTOM_VAR}, 0px)`,
    left: SAFE_AREA_LEFT,
    right: SAFE_AREA_RIGHT,
};

export interface ChromeLayout {
    mode: NavMode;
    tabBar: boolean;
    searchRow: boolean;
    /** A field outside the dock has focus: the dock is out of the way. */
    editing?: boolean;
}

export function chromeVars({
    mode,
    tabBar,
    searchRow,
    editing = false,
}: ChromeLayout): {
    top: string;
    bottom: string;
} {
    if (mode !== 'bottom')
        return { top: `${HEADER_HEIGHT_PX}px`, bottom: '0px' };
    const top = `calc(${TOP_BAR_HEIGHT_PX}px + env(safe-area-inset-top, 0px))`;
    // The keyboard is up and the dock steps aside (useEditingOutsideDock):
    // nothing is reserved, and on Android the layout viewport already ends
    // above the keyboard.
    if (editing) return { top, bottom: '0px' };
    // The search row's height stays reserved while it is scrolled away, so the
    // page does not jump when it comes back.
    const bottomPx =
        (tabBar ? TAB_BAR_HEIGHT_PX : 0) +
        (searchRow ? SEARCH_ROW_HEIGHT_PX : 0);
    return {
        top,
        bottom: `calc(${bottomPx}px + env(safe-area-inset-bottom, 0px))`,
    };
}

// Pages whose main control is their own search field; the phone search row
// would only be a second search box there.
const ROUTES_WITH_OWN_SEARCH = ['/markets/search'];

export const hasOwnSearch = (pathname: string) =>
    ROUTES_WITH_OWN_SEARCH.some(p => matchPath(p, pathname) !== null);
